'use client';

import { EmptySubscription } from '@ahamove/icons/empty';
import { ArrowLeftIcon, QuestionIcon } from '@ahamove/icons/subscription';
import {
  Group,
  Skeleton,
  Button,
  Text,
  rem,
  Card,
  ActionIcon,
} from '@mantine/core';
import { useParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import type { PropsWithChildren } from 'react';
import { useGetSubscriptionDetail } from './api';
import SubscriptionDetailContent from './components/subscription-detail-content';
import { ResultBlock } from '@/components/result-block';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { Link, useRouter } from '@/navigation';

export default function SubscriptionDetailContainer() {
  const locale = useLocale();
  const isMobile = useIsMobile();
  const tSystem = useTranslations('system');
  const { id } = useParams();
  const { data, isLoading, error } = useGetSubscriptionDetail(id as string, {
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <SubscriptionDetailWrapper>
        <Skeleton w="100%" h={isMobile ? 160 : 240} animate radius={0} />
        <Card
          mt={isMobile ? -16 : -24}
          py={isMobile ? rem(16) : rem(24)}
          radius={isMobile ? rem(12) : rem(24)}
          style={{ zIndex: 2 }}
        >
          <Skeleton w="50%" h={24} animate mb="sm" />
          <Skeleton w="80%" h={16} animate />
        </Card>
      </SubscriptionDetailWrapper>
    );
  }

  if (error?.response?.data)
    return (
      <SubscriptionDetailWrapper>
        <ResultBlock
          style={{
            width: isMobile ? '100%' : '897px',
          }}
          className="justify-start p-[24px] md:p-0"
          icon={EmptySubscription}
          title={error?.response?.data?.title ?? tSystem('error.title')}
          description={
            <Text size={isMobile ? 'sm' : 'md'} c="neutral.6">
              {error?.response?.data?.description ??
                tSystem('error.unknownError')}
            </Text>
          }
        >
          <Group mt="xl" justify="center">
            <Button
              component={Link}
              href={'/subscriptions'}
              variant="light"
              color="neutral"
              size="sm"
              miw={192}
            >
              {tSystem('links.backToHomepage')}
            </Button>
          </Group>
        </ResultBlock>
      </SubscriptionDetailWrapper>
    );
  if (!data) return null; // TODO: handle empty data

  return (
    <SubscriptionDetailWrapper
      termAndConditionUrl={data?.data?.term_and_condition_url}
      title={data?.data?.title[locale]}
    >
      <SubscriptionDetailContent data={data?.data} />
    </SubscriptionDetailWrapper>
  );
}

export function SubscriptionDetailWrapper({
  children,
  title,
  termAndConditionUrl,
}: PropsWithChildren<{ title?: string; termAndConditionUrl?: string }>) {
  const router = useRouter();
  const handleRedirectSubscriptionList = () => {
    router.push('/subscriptions');
  };

  return (
    <>
      <Group
        visibleFrom="sm"
        pos="sticky"
        top={0}
        left={0}
        justify="center"
        mih={rem(64)}
        w="100%"
        px={rem(48)}
        py={rem(12)}
        bg="white"
        styles={{
          root: {
            zIndex: 100,
            borderBottom: `1px solid var(--mantine-color-neutral-1)`,
          },
        }}
      >
        <ActionIcon
          pos="absolute"
          left={0}
          size={rem(44)}
          variant="transparent"
          onClick={handleRedirectSubscriptionList}
          c="neutral.6"
          style={{
            outline: 'none',
          }}
        >
          <ArrowLeftIcon width={24} height={24} />
        </ActionIcon>
        <Text lineClamp={1} size="xl" fw={700}>
          {title ?? ''}
        </Text>
        {termAndConditionUrl ? (
          <ActionIcon
            component="a"
            href={termAndConditionUrl}
            target="_blank"
            pos="absolute"
            right={rem(12)}
            size={rem(44)}
            variant="transparent"
            c="neutral.6"
            style={{
              outline: 'none',
            }}
          >
            <QuestionIcon width={32} height={32} />
          </ActionIcon>
        ) : null}
      </Group>
      {children}
    </>
  );
}
